<template>
  <v-container fluid class="gpr-home">
    <section class="gpr-home__welcome">
      <div class="gpr-home__identity">
        <v-avatar class="gpr-home__avatar" size="64">
          <span>{{ userInitials }}</span>
        </v-avatar>
        <div>
          <p class="gpr-home__eyebrow">GPR Suite</p>
          <h1>Bienvenido, {{ userName }}</h1>
          <p class="gpr-home__subtitle">Gestión estratégica y seguimiento empresarial</p>
        </div>
      </div>
      <p class="gpr-home__copy">
        Accede rápidamente a los módulos principales disponibles para tu perfil y continúa el seguimiento de la gestión.
      </p>
    </section>

    <section class="gpr-home__quick" aria-label="Accesos rápidos">
      <article
        v-for="item in visibleQuickAccesses"
        :key="item.title"
        class="gpr-home-card"
        role="button"
        tabindex="0"
        @click="goToModule(item)"
        @keydown.enter.prevent="goToModule(item)"
        @keydown.space.prevent="goToModule(item)"
      >
        <div class="gpr-home-card__icon">
          <v-icon>{{ item.icon }}</v-icon>
        </div>
        <div class="gpr-home-card__content">
          <h2>{{ item.title }}</h2>
          <p>{{ item.description }}</p>
        </div>
        <v-icon class="gpr-home-card__arrow">mdi-arrow-right</v-icon>
      </article>
    </section>
  </v-container>
</template>

<script>
import SidebarItems from '@/layouts/full-layout/sidebar/SidebarItems';
import { decryptAES } from '@/components4x/utils/utils4x';

export default {
  name: 'Inicio',

  data: () => ({
    quickAccessConfig: [
      {
        moduleCode: 'gh-proy',
        childCode: 'gh-proy-mis_proy',
        title: 'Proyectos',
        description: 'Consulta tus proyectos y su matriz de seguimiento.',
        icon: 'mdi-folder-multiple-outline'
      },
      {
        moduleCode: 'gh-pres',
        childCode: 'gh-pres-dash',
        title: 'Presupuesto',
        description: 'Revisa el dashboard y los archivos del presupuesto.',
        icon: 'mdi-cash-multiple'
      },
      {
        moduleCode: 'gh-act',
        childCode: 'gh-act-mat_aac',
        title: 'Actas',
        description: 'Da seguimiento a acuerdos, compromisos y actas.',
        icon: 'mdi-file-document-multiple-outline'
      },
      {
        moduleCode: 'gh-cal',
        childCode: 'gh-cal-com_per',
        title: 'Calendario y comités',
        description: 'Gestiona comités, eventos y reuniones programadas.',
        icon: 'mdi-calendar-month-outline'
      }
    ]
  }),

  computed: {
    visibleQuickAccesses() {
      return this.quickAccessConfig
        .map((item) => {
          const route = this.findSidebarRoute(item.moduleCode, item.childCode);

          return {
            ...item,
            route
          };
        })
        .filter((item) => item.route && this.verifyAuthAcl(item.moduleCode) && this.verifyAuthAcl(item.childCode));
    },
    userName() {
      const name = this.getLocalProfileValue('nc4rhtcc976');
      const user = this.getLocalProfileValue('user');
      const responsable = this.getLocalProfileValue('lrd5qwlr674');

      return name || user || responsable || 'usuario';
    },
    userInitials() {
      return this.userName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase();
    }
  },

  methods: {
    findSidebarRoute(moduleCode, childCode) {
      const module = SidebarItems.find((item) => item.meta && item.meta.code === moduleCode);
      if (!module || !module.children) return null;

      const child = module.children.find((item) => item.meta && item.meta.code === childCode);
      if (!child) return null;

      return `${module.group}/${child.to}`;
    },
    verifyAuthAcl(code) {
      if (this.$store.getters.getRole === '*') return true;
      if (code === '*') return true;

      const authorization = [...this.$store.getters.getPermissions, ...this.$store.getters.getProfile];
      return authorization.indexOf(code) !== -1;
    },
    goToModule(item) {
      if (!item.route) return;
      this.$router.push({ path: item.route });
    },
    safeDecrypt(value) {
      if (!value) return '';

      try {
        return decryptAES(value);
      } catch (error) {
        void error;
        return '';
      }
    },
    isReadableProfileValue(value) {
      return (
        typeof value === 'string' && value.trim() !== '' && value.length <= 90 && !/^[A-Za-z0-9+/=]{48,}$/.test(value)
      );
    },
    getLocalProfileValue(key) {
      const raw = localStorage.getItem(key) || '';
      const decrypted = this.safeDecrypt(raw);

      if (this.isReadableProfileValue(decrypted)) return decrypted.trim();
      if (this.isReadableProfileValue(raw)) return raw.trim();

      return '';
    }
  }
};
</script>

<style lang="scss">
.gpr-home {
  min-height: calc(100vh - 64px - 60px);
  padding: 32px;
  background: #f8fafc;
}

.gpr-home__welcome {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
  margin: 0 auto 24px;
  max-width: 1180px;
  padding: 34px;
}

.gpr-home__identity {
  align-items: center;
  display: flex;
  gap: 18px;
}

.gpr-home__avatar {
  background: var(--gpr-primary-light, #fbecef);
  border: 1px solid rgba(196, 18, 48, 0.16);
  color: var(--gpr-primary, #c41230);
  font-size: 1.2rem;
  font-weight: 800;
}

.gpr-home__eyebrow {
  color: var(--gpr-primary, #c41230);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 6px;
  text-transform: uppercase;
}

.gpr-home h1 {
  color: #1f2937;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.18;
  margin: 0;
}

.gpr-home__subtitle {
  color: #64748b;
  font-size: 1rem;
  margin: 8px 0 0;
}

.gpr-home__copy {
  color: #475569;
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 22px 0 0;
  max-width: 720px;
}

.gpr-home__quick {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0 auto;
  max-width: 1180px;
}

.gpr-home-card {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  display: flex;
  gap: 14px;
  min-height: 160px;
  outline: none;
  padding: 22px;
  position: relative;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.gpr-home-card:hover,
.gpr-home-card:focus {
  border-color: rgba(196, 18, 48, 0.32);
  box-shadow: 0 18px 36px rgba(196, 18, 48, 0.11);
  transform: translateY(-2px);
}

.gpr-home-card:focus {
  box-shadow: 0 0 0 3px rgba(196, 18, 48, 0.15), 0 18px 36px rgba(196, 18, 48, 0.11);
}

.gpr-home-card__icon {
  align-items: center;
  background: var(--gpr-primary-light, #fbecef);
  border-radius: 10px;
  color: var(--gpr-primary, #c41230);
  display: flex;
  flex: 0 0 46px;
  height: 46px;
  justify-content: center;
  width: 46px;
}

.gpr-home-card__icon .v-icon {
  color: currentColor;
  font-size: 24px;
}

.gpr-home-card__content h2 {
  color: #1f2937;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.25;
  margin: 3px 28px 10px 0;
}

.gpr-home-card__content p {
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.45;
  margin: 0;
}

.gpr-home-card__arrow {
  color: var(--gpr-primary, #c41230) !important;
  font-size: 20px;
  opacity: 0;
  position: absolute;
  right: 18px;
  top: 24px;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.gpr-home-card:hover .gpr-home-card__arrow,
.gpr-home-card:focus .gpr-home-card__arrow {
  opacity: 1;
  transform: translateX(2px);
}

@media (max-width: 1260px) {
  .gpr-home__quick {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .gpr-home {
    padding: 20px;
  }

  .gpr-home__welcome {
    padding: 24px;
  }

  .gpr-home__identity {
    align-items: flex-start;
  }

  .gpr-home h1 {
    font-size: 1.55rem;
  }

  .gpr-home__quick {
    grid-template-columns: 1fr;
  }

  .gpr-home-card {
    min-height: 132px;
  }
}
</style>
